import type { TooltipContentProps } from 'recharts'

import { Text } from '@buffer-mono/popcorn'

import { ChartColorDot } from '../ChartColorDot'
import { useChartSeriesById } from '../ChartSeriesContext'

import { isNetChangeRow, signedNumber } from './buildNetChangeRows'

import styles from './NetChangeBarChart.module.css'

import type { JSX } from 'react'

/** The trailing "(±N.N%)" — signed and coloured by direction (green up, red
 *  down, neutral flat). Renders nothing when there's no baseline (start of 0). */
function NetChangePercent({ pct }: { pct: number | null }): JSX.Element | null {
  if (pct == null) return null
  const sign = pct >= 0 ? '+' : '−'
  const colorClass =
    pct > 0 ? styles.pctPositive : pct < 0 ? styles.pctNegative : undefined
  return (
    <span
      className={colorClass}
    >{` (${sign}${Math.abs(pct).toFixed(1)}%)`}</span>
  )
}

type NetChangeTooltipViewProps = Pick<
  Partial<TooltipContentProps<number, string>>,
  'active' | 'payload'
>

/**
 * Context-driven tooltip for the diverging net-change chart. Reads the hovered
 * row from Recharts and looks its series up in {@link ChartSeriesContext} for the
 * colour, icon, and label — so the tooltip can't drift from the bar it describes.
 * Shows the signed net change, the channel, and a `start → end (±%)` detail line.
 */
export function NetChangeTooltipView({
  active,
  payload,
}: NetChangeTooltipViewProps): JSX.Element | null {
  const raw: unknown = payload?.[0]?.payload
  const row = isNetChangeRow(raw) ? raw : undefined
  const series = useChartSeriesById(row?.id ?? '')

  if (!active || row == null || series == null) return null

  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipRow}>
        <ChartColorDot color={series.color} />
        <Text size="sm" weight="bold">
          {signedNumber(row.delta)}
        </Text>
        {series.icon}
        <Text size="sm">{series.label}</Text>
      </div>
      <Text size="sm" className={styles.tooltipSub}>
        {`${row.start.toLocaleString()} → ${row.end.toLocaleString()}`}
        <NetChangePercent pct={row.pct} />
      </Text>
    </div>
  )
}
